import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AcceptedResourceThumbnail,
  useGenerateThumbnailPanel,
} from "../../hooks/useGenerateThumbnailPanel/useGenerateThumbnailPanel";
import { ThumbnailGeneratingAnimation } from "./ThumbnailGeneratingAnimation";

interface GenerateThumbnailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialTitle: string;
  initialDescription: string;
  onAccepted: (thumbnail: AcceptedResourceThumbnail) => void;
}

export const GenerateThumbnailPanel: React.FC<GenerateThumbnailPanelProps> = ({
  isOpen,
  onClose,
  initialTitle,
  initialDescription,
  onAccepted,
}) => {
  const {
    title,
    setTitle,
    description,
    setDescription,
    preview,
    previewSrc,
    isGenerating,
    isAccepting,
    canGenerate,
    generate,
    accept,
  } = useGenerateThumbnailPanel({
    isOpen,
    initialTitle,
    initialDescription,
    onAccepted,
  });

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-normal text-gray-900 dark:text-white">
            Generate Thumbnail
          </DialogTitle>
          <DialogDescription className="text-gray-500 dark:text-gray-400">
            The title and description are sent to the image generator. Nothing
            is saved until you accept a result.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label
              className="text-sm font-normal text-gray-900 dark:text-white"
              htmlFor="generate-thumbnail-title"
            >
              Title
            </label>
            <Input
              id="generate-thumbnail-title"
              onChange={(event) => setTitle(event.target.value)}
              placeholder="What is this resource about?"
              value={title}
            />
          </div>

          <div className="space-y-1.5">
            <label
              className="text-sm font-normal text-gray-900 dark:text-white"
              htmlFor="generate-thumbnail-description"
            >
              Description
            </label>
            <Textarea
              className="resize-none [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600"
              id="generate-thumbnail-description"
              onChange={(event) => setDescription(event.target.value)}
              placeholder="A sentence or two summarising the resource."
              rows={3}
              value={description}
            />
          </div>

          <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-950">
            {isGenerating && <ThumbnailGeneratingAnimation />}

            {!isGenerating && previewSrc && (
              <img
                alt={preview?.headline ?? "Generated thumbnail preview"}
                className="h-full w-full object-cover"
                src={previewSrc}
              />
            )}

            {!isGenerating && !previewSrc && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No preview yet.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            disabled={isGenerating || isAccepting}
            onClick={onClose}
            variant="ghost"
          >
            Cancel
          </Button>
          <Button disabled={!canGenerate} onClick={generate} variant="outline">
            {preview ? "Regenerate" : "Generate"}
          </Button>
          <Button
            disabled={!preview || isGenerating || isAccepting}
            onClick={accept}
          >
            {isAccepting ? "Saving…" : "Use this thumbnail"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
